/**
 * Result of discount calculation from coupon description.
 */
export type DiscountResult = {
  discountedPrice: number;
  discountedPricePerMg: number | null;
  discountPercent: number;
} | null;

/**
 * Calculate discount from coupon description.
 * 
 * Parses percentage from coupon_description (e.g., "10% off", "15% OFF")
 * and calculates discounted price and price per mg.
 * 
 * @param price - Original price
 * @param vialSizeMg - Vial size in mg (optional)
 * @param couponDescription - Coupon description text that may contain a percentage
 * @returns DiscountResult with discounted prices and percentage, or null if no valid discount
 */
export function getDiscountFromCoupon(
  price: number,
  vialSizeMg: number | null | undefined,
  couponDescription?: string | null
): DiscountResult {
  if (!couponDescription) return null;

  // Parse percentage from coupon description (case-insensitive)
  // Matches patterns like "10%", "15.5%", "10 %", etc.
  const match = couponDescription.match(/(\d+(\.\d+)?)\s*%/i);
  if (!match) return null;

  const percent = parseFloat(match[1]);
  if (isNaN(percent) || percent <= 0 || percent >= 100) return null;

  // Calculate discounted price
  const discountFactor = 1 - percent / 100;
  const discountedPrice = Number((price * discountFactor).toFixed(2));

  // Calculate discounted price per mg if vial size is available
  let discountedPricePerMg: number | null = null;
  if (vialSizeMg && vialSizeMg > 0) {
    discountedPricePerMg = Number((discountedPrice / vialSizeMg).toFixed(4));
  }

  return {
    discountedPrice,
    discountedPricePerMg,
    discountPercent: percent,
  };
}

